import { ReactComponent as IconPrevious } from '@/assets/svgs/previous-arrow.svg'
import { ReactComponent as IconChevron } from '@/assets/svgs/profile-chevron.svg'
import Button from '@/components/Button'
import Portal from '@/components/Portal'
import { ToastContext } from '@/context/toast/index'
import useConfig from '@/hooks/use-config/index'
import useEclips from '@/hooks/use-eclips/index'
import usePage from '@/hooks/use-page'
import useTemplates from '@/hooks/use-templates/index'
import { NavLink, useNavigate, useQuery } from '@/lib/router-dom/index'
import { useFormik } from 'formik'
import { orderBy, uniqBy } from 'lodash'
import { useContext, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { DataElementList } from './data-element-list.jsx'
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index.jsx'

const validationSchema = yup.object({
	name: yup
		.string('Type name')
		.required('Name is required')
		.nullable(),
	referenceNumber: yup
		.string('Enter Reference Number')
		.min(8, 'Reference Number should be minimum of 8 characters length')
		.max(32, 'Reference Number should be of 32 characters length')
		.required('Reference Number is required')
		.nullable(),
	templateId: yup
		.string('Select Template')
		.required('Template is required')
		.nullable(),
	recipientOrgId: yup
		.string('Select Participant')
		.required('Participant is required')
		.nullable(),
	message: yup
		.string('Type your message here')
		.optional()
		.nullable(),
	request: yup.bool()
})

export default function RequestPackageForm() {
	/*=================================================
			General
	/================================================*/
	const { consumeSystems, provideSystems } = useConfig()
	const { addToastMessage } = useContext(ToastContext)

	const { goTo } = useNavigate();
	const query = useQuery()
	const { t } = useTranslation()
	const { create, loading } = useEclips()
	const formik = useFormik({
		initialValues: {
			id: 'test',
			name: '',
			templateId: '',
			expectedDateOfCompletion: new Date(),
			referenceNumber: '',
			recipientOrgId: '',
			request: true,
		},
		validationSchema: validationSchema,
		onSubmit: async (data) => {
			const response = await create(data)
			goTo(`/eclip/shared`)
			addToastMessage('Eclip successfully created', 'success')
		},
	})

	const { fetch, items: templates } = useTemplates()
	const pageTitle = t('Request Package')
	const [recipients, setRecipients] = useState([])
	const [dataEle, setDataElements] = useState([])
	let selectedDataElement = [];

	function sortArray(array) {
		return orderBy(array, [(option) => option?.name.toLowerCase(), ['asc']])
	}

	useEffect(() => {
		const consumeOption = consumeSystems.map((item) => ({
			id: item.id,
			name: item.name,
		}))
		const provideOption = provideSystems.map((item) => ({
			id: item.id,
			name: item.name,
		}))

		const combineOptions = [...consumeOption, ...provideOption]
		const updatedOptions = uniqBy(combineOptions, 'id')

		setRecipients(sortArray(updatedOptions))
	}, [consumeSystems, provideSystems])

	const handleCancel = () => {
		goTo('/eclip')
	}

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Package Sharing',
				link: `/eclip/shared`
			},
			{
				label: 'Request Package',
				link: `/eclip/request`
			},
		],
	})


	const ErrorMessage = ({ name }) =>
		formik.errors[name] && formik.touched[name] ? (
		<div className="error-message">{formik.errors[name]}</div>
		) : null

	useEffect(() => {
		fetch({
			params: {
				org_id: formik.values.recipientOrgId,
			}
		})
	}, [formik.values.recipientOrgId])

	useEffect(() => {
		const selectedTemplateId = query.get('templateId')
		formik.setFieldValue('templateId', selectedTemplateId)
	}, [query])

	const getDataElements = (value) => {
		selectedDataElement = templates.filter((template) => template.id === value)

		setDataElements(selectedDataElement)
	}

	return (
		<div className="eclip-create__content">
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">{pageTitle}</div>
				</div>

				<div className="title-header__right"></div>
			</div>

			<form data-testid ="formsubmit" className="eclip-create__form" onSubmit={formik.handleSubmit}>
				<div className="eclip-create__form-row">
					<div className="eclip-create__input-label eclip-create__input-label--bold">{t('eclipName')}</div>
					<input
						className="eclip-create__input-field"
						name="name"
						placeholder={t('eclipNamePlaceholder')}
						value={formik.values.name}
						onChange={formik.handleChange}
						required
					/>
					<ErrorMessage name="name" />
				</div>
				<div className="eclip-create__form-row">
					<div className="eclip-create__input-label eclip-create__input-label--bold">{t('eclipReference')}</div>
					<input
						className="eclip-create__input-field"
						name="referenceNumber"
						placeholder={t('eclipReferencePlaceholder')}
						value={formik.values.referenceNumber}
						onChange={formik.handleChange}
						required
					/>
					<div className="eclip-create__input-description eclip-create__input-description--medium">
						{t('eclipReferenceDescription')}
					</div>
					<ErrorMessage name="referenceNumber" />
				</div>

				<div className="eclip-create__form-row">
					<div className="eclip-create__input-label eclip-create__input-label--bold">{t('eclipParticipant')}</div>
					<div className="select">
						<div className="select__icon">
							<IconChevron />
						</div>
						<div className="select__options">
							<select
								name="recipientOrgId"
								value={formik.values.recipientOrgId}
								onChange={formik.handleChange}
							>
								<option value="" disabled>
									{t('select')}
								</option>
								{recipients.map((recipient, index) => (
									<option key={index} value={recipient.id}>
										{recipient.name}
									</option>
								))}
							</select>
							<ErrorMessage name="recipientOrgId" />
						</div>
					</div>
				</div>
				<div className="eclip-create__form-row">
					<div className="eclip-create__input-label eclip-create__input-label--bold">{t('eclipTemplate')}</div>
					<div className="select">
						<div className="select__icon">
							<IconChevron />
						</div>
						<div className="select__options">
							<select
								data-testid="select"
								name="templateId"
								value={formik.values.templateId}
								onChange={(e) => {
									formik.handleChange(e);
									getDataElements(e.target.value)
								}}
								disabled={!formik.values.recipientOrgId}
							>
								<option value="" disabled>
									{t('select')}
								</option>
								{templates.map((template, index) => (
									<option key={index} value={template.id}>
										{template.name}
									</option>
								))}
							</select>
							<ErrorMessage name="templateId" />
						</div>
					</div>
				</div>
				<div className="eclip-create__form-row">
					<div className="eclip-create__input-label eclip-create__input-label--bold">{t('eclipDataElements')} ({dataEle[0]?.dataElements?.length < 9 ? '0'+dataEle[0]?.dataElements?.length : dataEle[0]?.dataElements?.length || '00'})</div>
					<div className='eclip-create__data-element-wrapper'>
						<DataElementList data={dataEle[0]} />
					</div>
				</div>
				<div className="eclip-create__form-row">
					<div className="eclip-create__input-label eclip-create__input-label--bold">{t('message')} <span className='eclip-create__input-label--optional'>(Optional)</span></div>
					<textarea
						className="eclip-create__input-field"
						name="message"
						placeholder={t('eclipMessagePlaceholder')}
						value={formik.values.message}
						onChange={formik.handleChange}
						rows={5}
					/>
					<ErrorMessage name="message" />
				</div>
				{loading &&
					ReactDOM.createPortal(
						<div className="modal white">
							<div className="loading">
								<img src="/loading.gif" />
							</div>
						</div>,
					document.body,
				)}
				<div className="DS__layout__footer">
					<div className="layout-default__sticky-actions">
						<div className="layout-default__sticky-actions__left">
							<Button
								text={t('Cancel')}
								sizing="Medium"
								full
								noborder
								maxWidth="154"
								submit={handleCancel}
							/>
						</div>
						<div
							className="layout-default__sticky-actions__right"
							style={{ display: 'flex' }}
						>
							<div className="overview__button_wrap">
							<Button
								data-testid="sendrequest"
								text={t('Send Request')}
								sizing="Medium"
								full
								maxWidth="154"
								type="submit"
							/>
							</div>
						</div>
					</div>
				</div>
			</form>
		</div>
	)
}
